import { CSSProperties, FC, Suspense, useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";

import { router } from "@/app/Routes/Routes";
import { TimeProvider } from "@/app/application/TimeContext";
import {
	TourBadge,
	TourClose,
	TourNextButton,
	TourPrevButton,
} from "@/app/application/Tour";
import { UserNotActive } from "@/app/application/UserNotActive";
// Import from component
import { ColorModeContext, useMode } from "@/app/application/theme";
// Import from model
import { TranslationProvider } from "@/app/providers";
import { UserContextProvider } from "@/app/providers";
import { UserCompanyConfigurationProvider } from "@/app/providers";
import { ProcedureContext } from "@/features/procedures/context";
import {
	Box,
	CssBaseline,
	LinearProgress,
	ThemeProvider,
	alpha,
} from "@mui/material";
import { TourProvider } from "@reactour/tour";

import useAuthentication from "@/auth/useAuthentication";

import { Language } from "@/model/Language/Language";
import { UserCompanyConfiguration } from "@/model/user-company-configuration/UserCompanyConfiguration";

// Import from service
import {
	checkIfAtLeastGuestAccess,
	useFetchActiveUserCompanyConfiguration,
} from "@/service/ApplicationQueries";

export const Satos: FC = () => {
	const [companyConfiguration, setCompanyConfiguration] =
		useState<UserCompanyConfiguration | null>(null);
	const [language, setLanguage] = useState<Language | null>(null);
	const [openFolders, setOpenFolders] = useState<string[]>([]);
	const [expandAll, setExpandAll] = useState<boolean>(false);
	const [activeStep, setActiveStep] = useState<number>(-1);
	const [activeChecklistId, setActiveChecklistId] = useState<number>(0);

	const authContext = useAuthentication();
	const user = authContext?.user ?? null;

	const { data: userCompanyConfiguration } =
		useFetchActiveUserCompanyConfiguration();

	useEffect(() => {
		if (user?.Company?.Language) {
			setLanguage(user.Company.Language);
		}
	}, [user]);

	useEffect(() => {
		if (userCompanyConfiguration) {
			setCompanyConfiguration(userCompanyConfiguration);
		}
	}, [userCompanyConfiguration]);

	const { theme, colorMode } = useMode();
	const { mode } = colorMode;

	// Sync theme mode + key palette tokens to DOM so non-React overlays (SweetAlert2) stay theme-aware.
	useEffect(() => {
		if (typeof document !== "undefined") {
			const root = document.documentElement;
			const rootStyles = window.getComputedStyle(root);
			const isDark = theme.palette.mode === "dark";
			const controlRadiusBase =
				typeof theme.shape.borderRadius === "number"
					? theme.shape.borderRadius + 2
					: 8;
			const appSurfaceRadius = rootStyles
				.getPropertyValue("--app-surface-radius")
				.trim();
			const appSurfaceBorderWidth = rootStyles
				.getPropertyValue("--app-surface-border-width")
				.trim();

			root.setAttribute("data-mui-color-scheme", mode);
			root.style.setProperty(
				"--satos-palette-background-default",
				theme.palette.background.default,
			);
			root.style.setProperty(
				"--satos-palette-background-paper",
				theme.palette.background.paper,
			);
			root.style.setProperty(
				"--satos-palette-text-primary",
				theme.palette.text.primary,
			);
			root.style.setProperty(
				"--satos-palette-text-secondary",
				theme.palette.text.secondary,
			);
			root.style.setProperty(
				"--satos-palette-primary",
				theme.palette.primary.main,
			);
			root.style.setProperty(
				"--satos-palette-success",
				theme.palette.success.main,
			);
			root.style.setProperty(
				"--satos-palette-warning",
				theme.palette.warning.main,
			);
			root.style.setProperty("--satos-palette-error", theme.palette.error.main);
			root.style.setProperty("--satos-palette-info", theme.palette.info.main);
			root.style.setProperty("--satos-palette-divider", theme.palette.divider);
			root.style.setProperty(
				"--satos-surface-radius",
				appSurfaceRadius ||
					`${typeof theme.shape.borderRadius === "number" ? theme.shape.borderRadius * 2 : 8}px`,
			);
			root.style.setProperty(
				"--satos-control-radius",
				`${controlRadiusBase}px`,
			);
			root.style.setProperty(
				"--satos-surface-border-width",
				appSurfaceBorderWidth || "1px",
			);
			root.style.setProperty(
				"--satos-alert-backdrop",
				alpha(theme.palette.background.default, isDark ? 0.8 : 0.22),
			);
			root.style.setProperty(
				"--satos-alert-shadow",
				isDark
					? "0 26px 90px rgba(0, 0, 0, 0.48)"
					: "0 22px 70px rgba(15, 23, 42, 0.18)",
			);
			root.style.setProperty(
				"--satos-toast-shadow",
				isDark
					? "0 14px 32px rgba(0, 0, 0, 0.34)"
					: "0 10px 24px rgba(15, 23, 42, 0.12)",
			);
			root.style.setProperty(
				"--satos-alert-footer-bg",
				alpha(theme.palette.background.default, isDark ? 0.52 : 0.72),
			);
			root.style.setProperty(
				"--satos-alert-field-bg",
				alpha(theme.palette.background.default, isDark ? 0.62 : 0.76),
			);
			root.style.setProperty(
				"--satos-alert-field-border",
				alpha(theme.palette.divider, isDark ? 0.82 : 0.98),
			);
			root.style.setProperty(
				"--satos-alert-field-border-hover",
				alpha(theme.palette.text.secondary, isDark ? 0.54 : 0.4),
			);
			root.style.setProperty(
				"--satos-alert-focus-ring",
				alpha(theme.palette.primary.main, isDark ? 0.24 : 0.16),
			);
			root.style.setProperty(
				"--satos-alert-focus-color",
				theme.palette.primary.main,
			);
			root.style.setProperty(
				"--satos-toast-track",
				alpha(theme.palette.text.secondary, isDark ? 0.24 : 0.2),
			);
		}
	}, [mode, theme]);

	return (
		<ColorModeContext.Provider value={colorMode}>
			<TimeProvider>
				<ThemeProvider theme={theme}>
					<TourProvider
						steps={[]}
						scrollSmooth
						className="reactour__popover satos-tour-popover"
						styles={{
							popover: (base) => ({
								...base,
								backgroundColor: theme.palette.background.paper,
								color: theme.palette.text.primary,
								border: `1px solid ${theme.palette.divider}`,
								borderRadius: 12,
								boxShadow: theme.shadows[8],
								padding: 20,
								paddingRight: 56,
								maxWidth: 380,
								fontFamily: theme.typography.fontFamily,
							}),
							maskWrapper: (base) => ({
								...base,
								color: theme.palette.text.primary,
							}),
							badge: (base) => ({
								...base,
								background: "transparent",
								padding: 0,
							}),
							close: (base) => ({
								...base,
								top: 12,
								right: 12,
								background: "transparent",
								border: "none",
							}),
							controls: (base) => ({
								...base,
								marginTop: 16,
								gap: 8,
								paddingTop: 16,
								borderTop: `1px solid ${theme.palette.divider}`,
							}),
							button: (base: CSSProperties, state: { disabled?: boolean }) => ({
								...base,
								background: "transparent",
								border: "none",
								opacity: state.disabled ? 0.5 : base.opacity,
							}),
							dot: (base, { current }) => ({
								...base,
								width: 8,
								height: 8,
								borderRadius: 999,
								backgroundColor: current
									? theme.palette.primary.main
									: theme.palette.action.disabledBackground,
								opacity: current ? 1 : 0.5,
								border: "none",
								cursor: "pointer",
							}),
						}}
						components={{
							Badge: TourBadge,
							Close: TourClose,
						}}
						nextButton={TourNextButton}
						prevButton={TourPrevButton}
						badgeContent={({ currentStep, totalSteps }) =>
							`${currentStep + 1} / ${totalSteps}`
						}
					>
						<CssBaseline />
						<TranslationProvider value={language}>
							<ProcedureContext.Provider
								value={{
									openFolders,
									setOpenFolders,
									expandAll,
									setExpandAll,
									activeStep,
									setActiveStep,
									activeChecklistId,
									setActiveChecklistId,
								}}
							>
								<UserContextProvider value={user}>
									<UserCompanyConfigurationProvider
										value={companyConfiguration}
									>
										{user && checkIfAtLeastGuestAccess(user) ? (
											<Suspense
												fallback={
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															minHeight: "100vh",
															bgcolor: "background.default",
														}}
													>
														<LinearProgress
															sx={{ width: "120px", borderRadius: 1 }}
														/>
													</Box>
												}
											>
												<RouterProvider router={router} />
											</Suspense>
										) : (
											<UserNotActive />
										)}
									</UserCompanyConfigurationProvider>
								</UserContextProvider>
							</ProcedureContext.Provider>
						</TranslationProvider>
					</TourProvider>
				</ThemeProvider>
			</TimeProvider>
		</ColorModeContext.Provider>
	);
};
